import supabase from './supabaseClient';

/**
 * Logs in the user with email and password.
 * @param {string} email - The user's email.
 * @param {string} password - The user's password.
 * @returns {Promise<object>} - The session and user data.
 */
const login = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    console.error('Supabase login error:', error.message);
    throw new Error(error.message || 'Invalid login credentials.');
  }
  
  if (!data.session) {
    throw new Error('Login successful, but no session was returned.');
  }

  return data;
};

/**
 * Logs out the current user.
 */
const logout = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('Supabase logout error:', error.message);
    // Don't throw here, as we want to proceed with client-side cleanup
  }
};

/**
 * Verifies the current user session.
 * @returns {Promise<object>} - An object indicating success and session data.
 */
const verifySession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();

  if (error || !session) {
    return { success: false, session: null, user: null, error };
  }

  return { success: true, session, user: session.user };
};

/**
 * Fetches the role for the currently authenticated user from the 'user_roles' table.
 * @returns {Promise<string>} - The user's role ('admin', 'content_creator', or 'user').
 */
const getUserRole = async () => {
  try {
    // 1. Get the currently authenticated user from Supabase auth
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error('Error fetching user from Supabase auth:', userError.message);
      throw new Error('Could not get user session.');
    }

    if (!user) {
      console.warn('getUserRole was called but no authenticated user was found.');
      return 'user'; // No user, so return default role
    }

    console.log(`Fetching role for user ID: ${user.id}`);

    // 2. Query the 'user_roles' table using the user's ID
    const { data: roleData, error: roleError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .in('role', ['admin', 'content_creator']) // Only allow these specific roles
      .single(); // We expect only one role entry per user

    if (roleError) {
      // 'PGRST116' is the PostgREST code for "Query returned 0 rows".
      // This is an expected case for users who exist in auth.users but not in user_roles.
      if (roleError.code === 'PGRST116') {
        console.warn(`User with ID ${user.id} and email ${user.email} has no valid role in 'user_roles' table. Defaulting to 'user'.`);
        return 'user';
      }
      
      // For any other database error (like RLS blocking the query), log it.
      console.error('Error fetching user role from database:', roleError.message);
      throw new Error('Failed to fetch user role due to a database error.');
    }

    if (!roleData || !roleData.role) {
        console.warn(`User with ID ${user.id} found, but role is null or invalid. Defaulting to 'user'.`);
        return 'user';
    }

    // 3. Return the role if found
    console.log(`Successfully fetched role: '${roleData.role}' for user ID: ${user.id}`);
    return roleData.role;

  } catch (error) {
    console.error('A critical error occurred in getUserRole:', error.message);
    // We return a safe default role instead of throwing, to prevent app crashes.
    // The calling code will redirect to /unauthorized based on this role.
    return 'user';
  }
};

const authService = {
  login,
  logout,
  verifySession,
  getUserRole,
};

export default authService;